/**
 * CompanyEnableModal — manage which companies a GLOBAL record is enabled for.
 *
 * Accumulates enable/disable changes locally (pendingChanges Map) and only calls
 * onConfirm when the user explicitly clicks "Confirmar cambios". Visually matches
 * MasterCrud's LinkModal.tsx (icon badge, item layout, footer) — the two differ
 * only in this accumulate-then-confirm flow vs LinkModal's immediate local toggle.
 *
 * @module components/modals/CompanyEnableModal
 * @see FMPV-MVWC-E004-S005
 */
import React, { useState, useMemo, useEffect } from 'react';
import { Building2 } from 'lucide-react';
import { XMarkIcon, MagnifyingGlassIcon } from '@heroicons/react/24/outline';
import { Button } from '../../../Button/Button';
import type { MasterPatternViewCompany } from '../../types/service.types';

export interface CompanyEnableChange {
  companyId: string;
  action: 'enable' | 'disable';
}

export interface CompanyEnableModalProps {
  isOpen: boolean;
  recordId: string;
  /** Human-readable code/name of the record being enabled — shown in the header so it's clear which record this is for when several are open in sequence. Omit to hide. */
  recordLabel?: string;
  companies: MasterPatternViewCompany[];
  assignedCompanyIds: string[];
  onClose: () => void;
  onConfirm: (changes: CompanyEnableChange[]) => void;
  isLoading: boolean;
  /** Translation function override — falls back to the current Spanish text when omitted. */
  t?: (key: string, defaultValue?: string) => string;
}

export function CompanyEnableModal({
  isOpen,
  recordLabel,
  companies,
  assignedCompanyIds,
  onClose,
  onConfirm,
  isLoading,
  t,
}: CompanyEnableModalProps): React.ReactElement | null {
  const tt = t ?? ((key: string, defaultValue?: string) => defaultValue ?? key);
  const [searchQuery, setSearchQuery] = useState('');
  const [pendingChanges, setPendingChanges] = useState<Map<string, 'enable' | 'disable'>>(
    new Map(),
  );

  // Reset pending changes and search every time the modal opens
  useEffect(() => {
    if (isOpen) {
      setPendingChanges(new Map());
      setSearchQuery('');
    }
  }, [isOpen]);

  const assignedSet = useMemo(() => new Set(assignedCompanyIds), [assignedCompanyIds]);

  const filteredCompanies = useMemo(
    () =>
      companies.filter(
        (c) =>
          c.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
          c.code.toLowerCase().includes(searchQuery.toLowerCase()),
      ),
    [companies, searchQuery],
  );

  // Altura del cuerpo SIEMPRE fija en 25rem, sin importar cuántas compañías haya (1, 3, 20...) —
  // así el modal nunca cambia de tamaño ni al escribir en el buscador (antes usaba max-h-[400px],
  // que encogía el modal al filtrar a pocos resultados) ni según cuántas compañías tenga el maestro.
  // En rem (no px) para que escale con el zoom/tamaño de fuente del usuario, y con un tope en vh
  // para no cortarse en pantallas bajas (laptops, ventanas chicas) — nunca más del 45% del alto visible.
  const bodyHeight = companies.length === 0 ? '0' : 'min(25rem, 45vh)';

  if (!isOpen) return null;

  function isEffectivelyAssigned(companyId: string): boolean {
    const pending = pendingChanges.get(companyId);
    if (pending === 'enable') return true;
    if (pending === 'disable') return false;
    return assignedSet.has(companyId);
  }

  function handleToggle(companyId: string, action: 'enable' | 'disable') {
    setPendingChanges((prev) => {
      const next = new Map(prev);
      const originallyAssigned = assignedSet.has(companyId);
      const wouldBeOriginal =
        (action === 'enable' && originallyAssigned) ||
        (action === 'disable' && !originallyAssigned);
      if (wouldBeOriginal) {
        next.delete(companyId);
      } else {
        next.set(companyId, action);
      }
      return next;
    });
  }

  function handleEnableAll() {
    setPendingChanges((prev) => {
      const next = new Map(prev);
      companies.forEach((c) => {
        if (!assignedSet.has(c.id) && next.get(c.id) !== 'enable') {
          next.set(c.id, 'enable');
        }
      });
      return next;
    });
  }

  function handleConfirm() {
    const changes: CompanyEnableChange[] = Array.from(pendingChanges.entries()).map(
      ([companyId, action]) => ({ companyId, action }),
    );
    onConfirm(changes);
  }

  const pendingCount = pendingChanges.size;

  // Botones por compañía — mismas clases que LinkModal.tsx (h-8 px-3 rounded-md text-xs font-semibold border).
  const btnAssign = 'inline-flex items-center h-8 px-3 rounded-md text-xs font-semibold border transition-all bg-white dark:bg-dark-bg-primary text-primary-600 border-[#93d1fd] hover:bg-[#eff8ff] dark:hover:bg-primary-900/20';
  const btnUnassign = 'inline-flex items-center h-8 px-3 rounded-md text-xs font-semibold border transition-all bg-error/10 text-error-content border-error-border hover:brightness-95 dark:bg-error/20';

  return (
    <div
      role="dialog"
      aria-modal="true"
      className="fixed inset-0 z-[9000] flex items-center justify-center p-4"
      style={{ background: 'rgba(15,23,42,0.45)', backdropFilter: 'blur(2px)' }}
    >
      <div className="bg-bg-primary dark:bg-dark-bg-primary border border-border-primary dark:border-dark-border-primary rounded-xl shadow-2xl flex flex-col w-full max-w-2xl overflow-hidden relative">

        {/* Botón Cerrar (X) — absolute, como LinkModal.tsx */}
        <div className="absolute right-4 top-4 z-10">
          <Button
            type="outline"
            size="xs"
            iconOnly
            leftIcon={<XMarkIcon className="w-4 h-4" />}
            onClick={onClose}
            ariaLabel={tt('close', 'Cerrar')}
            className="!bg-white dark:!bg-dark-bg-primary"
          />
        </div>

        {/* Header — icono de edificio (lucide Building2), como en el diseño original */}
        <div className="p-[20px] flex gap-5 items-center">
          <div className="bg-[#dbeefe] dark:bg-primary-900/20 flex items-center justify-center rounded-full shrink-0 size-[40px]">
            <Building2 size={24} className="text-primary-600 dark:text-primary-400" />
          </div>
          <div className="flex flex-col justify-center">
            {/* Suffix format (" · {label}") and uniform styling match MasterCrud's
                LinkModal.tsx getDynamicTitle() exactly — a middle dot, not a dash, and
                no secondary color/weight split between the two parts. */}
            <h3 className="text-[20px] font-bold leading-[28px] tracking-tight text-content-primary dark:text-dark-content-primary">
              {tt('companyEnableTitle', 'Habilitar por compañía')}
              {recordLabel && ` · ${recordLabel}`}
            </h3>
            <p className="text-[14px] font-normal italic leading-[20px] text-content-tertiary dark:text-dark-content-tertiary">
              {tt('companyEnableDesc', 'Gestiona en qué compañías está habilitado este registro.')}
            </p>
          </div>
        </div>

        {/* Buscador (fijo, no scrollea con la lista) */}
        <div className="px-[20px] pb-[12px]">
          <div className="relative">
            <MagnifyingGlassIcon className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-content-tertiary dark:text-dark-content-tertiary" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder={tt('companySearchPlaceholder', 'Buscar compañía…')}
              aria-label={tt('companySearchPlaceholder', 'Buscar compañía…')}
              autoComplete="off"
              data-testid="company-search-input"
              className="w-full h-10 pl-9 pr-9 rounded-lg border border-border-primary dark:border-dark-border-primary bg-white dark:bg-dark-bg-primary text-sm text-content-primary dark:text-dark-content-primary placeholder:text-content-tertiary focus:outline-none focus:border-[#93d1fd] focus:ring-[3px] focus:ring-primary-500/10 transition-colors"
            />
            {searchQuery && (
              <button
                type="button"
                onClick={() => setSearchQuery('')}
                aria-label={tt('clear', 'Limpiar')}
                className="absolute right-2 top-1/2 -translate-y-1/2 inline-flex items-center justify-center w-6 h-6 rounded-md text-content-tertiary hover:text-content-primary hover:bg-background-secondary dark:hover:bg-dark-bg-secondary transition-colors"
              >
                <XMarkIcon className="w-3.5 h-3.5" />
              </button>
            )}
          </div>
        </div>

        {/* Body — lista de compañías, cada una en su propia tarjeta con borde (igual a LinkModal.tsx).
            Altura fija (bodyHeight, calculada arriba) en vez de max-h-[400px]: con max-h el
            contenedor se encogía al tamaño del resultado filtrado — al buscar y quedar pocas
            compañías, el modal entero "saltaba" más chico y el buscador terminaba pegado a las
            tarjetas. Con altura fija — igual a como se veía con la lista completa sin filtrar —
            el modal mantiene siempre el mismo tamaño. */}
        <div
          className="px-[20px] pb-[20px] flex flex-col gap-[20px] overflow-y-auto"
          style={{ height: bodyHeight }}
        >
          <div className="flex flex-col items-start w-full">
            {filteredCompanies.map((company) => {
              const effective = isEffectivelyAssigned(company.id);
              return (
                <div
                  key={company.id}
                  className="border border-border-primary dark:border-dark-border-primary h-[64px] px-[8px] flex items-center justify-between rounded-[8px] w-full mb-3 last:mb-0 transition-colors hover:border-[#93d1fd] hover:bg-[#eff8ff]/40 dark:hover:bg-primary-900/10"
                >
                  <div className="flex items-center gap-[8px]">
                    <div className="bg-[#dbeefe] dark:bg-primary-900/20 flex items-center justify-center rounded-[16px] shrink-0 size-[32px]">
                      <Building2 size={18} className="text-primary-600 dark:text-primary-400" />
                    </div>
                    <span className="text-[16px] font-medium leading-[24px] text-content-primary dark:text-dark-content-primary truncate max-w-[300px]">
                      {company.name}
                    </span>
                    <span className="text-[11px] text-content-secondary dark:text-dark-content-secondary flex-shrink-0">{company.code}</span>
                  </div>

                  {effective ? (
                    <button
                      type="button"
                      onClick={() => handleToggle(company.id, 'disable')}
                      disabled={isLoading}
                      className={btnUnassign}
                      data-testid={`disable-company-${company.id}`}
                    >
                      {tt('disable', 'Deshabilitar')}
                    </button>
                  ) : (
                    <button
                      type="button"
                      onClick={() => handleToggle(company.id, 'enable')}
                      disabled={isLoading}
                      className={btnAssign}
                      data-testid={`enable-company-${company.id}`}
                    >
                      {tt('enable', 'Habilitar')}
                    </button>
                  )}
                </div>
              );
            })}

            {/* Estado vacío del buscador */}
            {filteredCompanies.length === 0 && (
              <div className="w-full flex flex-col items-center justify-center gap-2 py-10 text-center">
                <MagnifyingGlassIcon className="w-6 h-6 text-content-tertiary dark:text-dark-content-tertiary" />
                <p className="text-sm text-content-secondary dark:text-dark-content-secondary">
                  {tt('noCompaniesFound', 'No se encontraron compañías')}
                </p>
                {searchQuery && (
                  <p className="text-xs text-content-tertiary dark:text-dark-content-tertiary">
                    <span className="font-medium text-content-primary dark:text-dark-content-primary">&ldquo;{searchQuery}&rdquo;</span>
                  </p>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Footer — Cancelar / Habilitar todas / Confirmar, igual a LinkModal.tsx */}
        <div className="px-[24px] py-[16px] pb-[20px] flex items-center justify-end gap-3 shrink-0 w-full border-t border-border-secondary dark:border-dark-border-primary">
          <Button type="plain" size="base" className="!h-9 !px-3 !text-sm font-medium" onClick={onClose} disabled={isLoading} data-testid="company-enable-cancel-button">
            {tt('cancel', 'Cancelar')}
          </Button>
          <Button type="outline" size="base" className="!h-9 !px-3 !text-sm font-medium" onClick={handleEnableAll} disabled={isLoading} data-testid="enable-all-button">
            {tt('enableAllCompanies', 'Habilitar todas')}
          </Button>
          <Button
            type="default"
            size="base"
            className="!h-9 !px-3 !text-sm font-medium"
            onClick={handleConfirm}
            disabled={pendingCount === 0 || isLoading}
            data-testid="confirm-changes-button"
          >
            {isLoading
              ? tt('saving', 'Guardando…')
              : pendingCount > 0
                ? tt('confirmChangesCount', 'Confirmar cambios ({count})').replace('{count}', String(pendingCount))
                : tt('confirmChanges', 'Confirmar cambios')}
          </Button>
        </div>
      </div>
    </div>
  );
}
